<section class="page">
  <header class="page-header">
    <div>
      <a routerLink="/users" class="back">← Users</a>
      <h1>User detail</h1>
      <p>Profile, wallets, beneficiaries, cards and transactions</p>
    </div>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  @if (loading()) {
    <div class="panel muted">Loading…</div>
  } @else {
    @if (user(); as u) {
      <div class="detail-grid">
        <div class="panel">
          <div class="panel-head">
            <h2>{{ fullName(u) }}</h2>
            <span class="badge" [attr.data-status]="u.kyc_status">{{ u.kyc_status || '—' }}</span>
          </div>

          <dl class="meta">
            <div><dt>ID</dt><dd>{{ u.id }}</dd></div>
            <div><dt>Email</dt><dd>{{ u.email || '—' }}</dd></div>
            <div><dt>Phone</dt><dd>{{ u.phone || '—' }}</dd></div>
            <div><dt>DOB</dt><dd>{{ u.date_of_birth || '—' }}</dd></div>
            <div><dt>Address</dt>
              <dd>
                {{ u.address_line1 || '—' }}
                @if (u.address_line2) { , {{ u.address_line2 }} }
                @if (u.city) { , {{ u.city }} }
                @if (u.post_code) { {{ u.post_code }} }
                @if (u.country) { , {{ u.country }} }
              </dd>
            </div>
            <div><dt>Kobble user</dt><dd class="mono">{{ u.kobble_user_id || 'n/a' }}</dd></div>
            <div><dt>Kobble status</dt><dd>{{ u.kobble_status || '—' }}</dd></div>
            <div><dt>Status</dt><dd>{{ u.status }}</dd></div>
            <div><dt>Points balance</dt><dd>{{ u.points_balance ?? u.reward_balance ?? 0 }}</dd></div>
            <div><dt>Points earned / redeemed</dt>
              <dd>{{ u.total_points_earned ?? 0 }} / {{ u.total_points_redeemed ?? 0 }}</dd>
            </div>
            <div>
              <dt>Login lock</dt>
              <dd>
                @if (u.is_permanently_locked) {
                  <span class="badge" data-status="rejected">Permanently blocked</span>
                } @else if (u.is_temporarily_locked) {
                  <span class="badge" data-status="pending">Temporarily locked</span>
                  @if (u.locked_until) {
                    <span class="muted"> until {{ u.locked_until | date: 'medium' }}</span>
                  }
                } @else {
                  <span class="muted">Not locked</span>
                }
                <span class="muted"> · fails: {{ u.failed_login_attempts ?? 0 }}</span>
              </dd>
            </div>
          </dl>

          <div class="reject-box">
            <h3>Unblock login</h3>
            <p class="muted statement-hint">
              Clears failed password attempts and temporary/permanent login locks so the user can sign in again.
              Use this after a permanent block (contact-support) or a 30-minute temp lock.
            </p>
            <div class="actions">
              <button
                class="btn btn-primary"
                type="button"
                (click)="unlockLogin()"
                [disabled]="saving() || unlockingLogin() || !isLoginLocked(u)"
                [title]="isLoginLocked(u) ? 'Clear login lock' : 'User is not locked'"
              >
                {{ unlockingLogin() ? 'Unblocking…' : 'Unblock user' }}
              </button>
            </div>
          </div>

          <div class="reject-box">
            <h3>Email transaction statement</h3>
            <p class="muted statement-hint">
              Sends a PDF or CSV to the user’s registered email. Max range: 1 year (366 days).
            </p>
            <label>
              <span>From</span>
              <input type="date" [(ngModel)]="statementDateFrom" />
            </label>
            <label>
              <span>To</span>
              <input type="date" [(ngModel)]="statementDateTo" />
            </label>
            <label>
              <span>Format</span>
              <select [(ngModel)]="statementFormat">
                <option value="pdf">PDF</option>
                <option value="csv">CSV</option>
              </select>
            </label>
            <div class="actions">
              <button
                class="btn btn-primary"
                type="button"
                (click)="confirmEmailStatement()"
                [disabled]="
                  saving() ||
                  emailingStatement() ||
                  !u.email ||
                  !statementDateFrom ||
                  !statementDateTo
                "
                [title]="u.email ? 'Email statement to ' + u.email : 'User has no email'"
              >
                {{ emailingStatement() ? 'Sending…' : 'Email statement' }}
              </button>
            </div>
          </div>

          <div class="reject-box">
            <h3>Adjust points</h3>
            <label>
              <span>Amount</span>
              <input type="number" min="1" step="1" [(ngModel)]="pointsAmount" />
            </label>
            <label>
              <span>Direction</span>
              <select [(ngModel)]="pointsDirection">
                <option value="credit">Credit (add)</option>
                <option value="debit">Debit (subtract)</option>
              </select>
            </label>
            <label>
              <span>Reason</span>
              <input type="text" [(ngModel)]="pointsReason" maxlength="255" placeholder="Missed card spend" />
            </label>
            <div class="actions">
              <button
                class="btn btn-primary"
                type="button"
                (click)="confirmPointsAdjust()"
                [disabled]="saving() || pointsAmount < 1 || !pointsReason.trim()"
              >
                Apply points
              </button>
            </div>
          </div>

          <div class="actions">
            <button class="btn btn-primary" type="button" (click)="approve()" [disabled]="saving()">
              Approve KYC
            </button>
            <button class="btn btn-danger" type="button" (click)="openReject()" [disabled]="saving()">
              Reject KYC
            </button>
          </div>

          @if (showReject()) {
            <div class="reject-box">
              <label>
                <span>Rejection reason</span>
                <textarea [(ngModel)]="rejectionReason" rows="3" placeholder="Document unclear"></textarea>
              </label>
              <div class="actions">
                <button class="btn btn-danger" type="button" (click)="confirmReject()" [disabled]="saving() || !rejectionReason.trim()">
                  Confirm reject
                </button>
                <button class="btn btn-secondary" type="button" (click)="cancelReject()" [disabled]="saving()">
                  Cancel
                </button>
              </div>
            </div>
          }
        </div>

        <div class="panel">
          <h2>KYC documents</h2>
          @if (!u.kyc_documents?.length) {
            <p class="muted">No documents</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Number</th>
                    <th>Status</th>
                    <th>File</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  @for (doc of u.kyc_documents; track doc.id) {
                    <tr>
                      <td>{{ doc.document_type }}</td>
                      <td>{{ doc.document_number || '—' }}</td>
                      <td>
                        <span class="badge" [attr.data-status]="doc.status">{{ doc.status }}</span>
                      </td>
                      <td>
                        @if (doc.file_url) {
                          <a [href]="doc.file_url" target="_blank" rel="noopener">View</a>
                        } @else {
                          —
                        }
                      </td>
                      <td>{{ doc.created_at ? (doc.created_at | date: 'mediumDate') : '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>

        <div class="panel full">
          <h2>Wallets</h2>
          @if (!u.wallets?.length) {
            <p class="muted">No wallets</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Kobble wallet</th>
                    <th>Holder</th>
                    <th>Product</th>
                    <th>Account</th>
                    <th>BSB / code</th>
                    <th>Balance</th>
                    <th>Available</th>
                    <th>Reserved</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (w of u.wallets; track w.id) {
                    <tr>
                      <td>{{ w.id }}</td>
                      <td class="mono">{{ w.kobble_wallet_id || '—' }}</td>
                      <td class="mono">{{ w.kobble_holder_id || '—' }}</td>
                      <td>{{ w.partner_product || '—' }}</td>
                      <td class="mono">{{ w.account_number || '—' }}</td>
                      <td class="mono">{{ w.bank_code || '—' }}{{ w.bank_code_type ? ' (' + w.bank_code_type + ')' : '' }}</td>
                      <td>{{ w.balance ?? 0 }} {{ w.currency || 'AUD' }}</td>
                      <td>{{ w.available_balance ?? 0 }}</td>
                      <td>{{ w.reserved_balance ?? 0 }}</td>
                      <td>{{ w.status || '—' }}</td>
                      <td>
                        <button
                          class="btn btn-secondary"
                          type="button"
                          (click)="openCredit(w.id, w.kobble_wallet_id)"
                          [disabled]="saving() || !w.kobble_wallet_id"
                          [title]="w.kobble_wallet_id ? 'Credit via Kobble' : 'No Kobble wallet linked'"
                        >
                          Credit
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (creditWalletId(); as walletId) {
              <div class="reject-box">
                <p>
                  <strong>Manual credit</strong> for wallet #{{ walletId }}
                  @if (creditKobbleWalletId(); as kid) {
                    <span class="muted mono"> → Kobble {{ kid }}</span>
                  }
                </p>
                <label>
                  <span>Amount</span>
                  <input type="number" [(ngModel)]="creditAmount" min="0.01" step="0.01" />
                </label>
                <label>
                  <span>Reference</span>
                  <input type="text" [(ngModel)]="creditReference" />
                </label>
                <div class="actions">
                  <button
                    class="btn btn-primary"
                    type="button"
                    (click)="confirmCredit()"
                    [disabled]="saving() || creditAmount <= 0"
                  >
                    Confirm credit
                  </button>
                  <button class="btn btn-secondary" type="button" (click)="cancelCredit()" [disabled]="saving()">
                    Cancel
                  </button>
                </div>
              </div>
            }
          }
        </div>

        <div class="panel full">
          <h2>Bank accounts</h2>
          @if (!u.bank_accounts?.length) {
            <p class="muted">No bank accounts</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Kobble account</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>BSB</th>
                    <th>Sort code</th>
                    <th>IBAN</th>
                    <th>Currency</th>
                    <th>Primary</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (acc of u.bank_accounts; track acc.id) {
                    <tr>
                      <td>{{ acc.id }}</td>
                      <td class="mono">{{ acc.kobble_account_id || '—' }}</td>
                      <td>{{ acc.account_name || '—' }}</td>
                      <td class="mono">{{ acc.account_number || '—' }}</td>
                      <td class="mono">{{ acc.bsb || '—' }}</td>
                      <td class="mono">{{ acc.sort_code || '—' }}</td>
                      <td class="mono">{{ acc.iban || '—' }}</td>
                      <td>{{ acc.currency || '—' }}</td>
                      <td>{{ acc.is_primary ? 'yes' : 'no' }}</td>
                      <td>{{ acc.status || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>

        <div class="panel full">
          <h2>Beneficiaries</h2>
          @if (!u.beneficiaries?.length) {
            <p class="muted">No beneficiaries</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Kobble ID</th>
                    <th>Name</th>
                    <th>Nickname</th>
                    <th>Account</th>
                    <th>BSB</th>
                    <th>Bank</th>
                    <th>PayID</th>
                    <th>Landlord</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (b of u.beneficiaries; track b.id) {
                    <tr>
                      <td>{{ b.id }}</td>
                      <td class="mono">{{ b.kobble_beneficiary_id || '—' }}</td>
                      <td>{{ b.name || '—' }}</td>
                      <td>{{ b.nickname || '—' }}</td>
                      <td class="mono">{{ b.account_number || b.iban || '—' }}</td>
                      <td class="mono">{{ b.bsb || b.bank_code || b.sort_code || '—' }}</td>
                      <td>{{ b.bank_name || '—' }}</td>
                      <td class="mono">{{ b.pay_id || '—' }}{{ b.pay_id_type ? ' (' + b.pay_id_type + ')' : '' }}</td>
                      <td>{{ b.is_landlord ? 'yes' : 'no' }}</td>
                      <td>{{ b.status || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>

        <div class="panel full">
          <h2>Cards</h2>
          @if (!u.cards?.length) {
            <p class="muted">No cards</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Kobble card</th>
                    <th>Type</th>
                    <th>Brand</th>
                    <th>Last 4</th>
                    <th>Expiry</th>
                    <th>Wallet</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (c of u.cards; track c.id) {
                    <tr>
                      <td>{{ c.id }}</td>
                      <td class="mono">{{ c.kobble_card_id || '—' }}</td>
                      <td>{{ c.card_type || '—' }}</td>
                      <td>{{ c.card_brand || '—' }}</td>
                      <td>•••• {{ c.last_four || '—' }}</td>
                      <td>{{ c.expiry_month || '—' }}/{{ c.expiry_year || '—' }}</td>
                      <td class="mono">{{ c.kobble_wallet_id || '—' }}</td>
                      <td>{{ c.status || '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>

        <div class="panel full">
          <div class="panel-head">
            <h2>Transactions</h2>
            <span class="muted">{{ u.transactions_total ?? u.transactions?.length ?? 0 }} total · showing latest {{ u.transactions?.length || 0 }}</span>
          </div>
          @if (!u.transactions?.length) {
            <p class="muted">No transactions</p>
          } @else {
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Leg</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>From → To</th>
                    <th>Beneficiary</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Kobble tx</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  @for (tx of u.transactions; track tx.id) {
                    <tr class="clickable" [routerLink]="['/transactions', tx.id]">
                      <td>{{ tx.id }}</td>
                      <td>{{ tx.leg || '—' }}</td>
                      <td>{{ tx.type || '—' }}</td>
                      <td>{{ tx.amount }} {{ tx.currency || '' }}</td>
                      <td class="mono small">
                        {{ tx.from_kobble_wallet_id || tx.wallet_id || '—' }}
                        →
                        {{ tx.to_party_type || '—' }}
                        {{ tx.to_kobble_wallet_id || tx.merchant_kobble_wallet_id || '' }}
                      </td>
                      <td>{{ tx.beneficiary?.name || tx.counterparty_name || (tx.beneficiary_id ? '#' + tx.beneficiary_id : '—') }}</td>
                      <td>
                        <span class="badge" [attr.data-status]="tx.status">{{ tx.status || '—' }}</span>
                      </td>
                      <td>{{ tx.reference || '—' }}</td>
                      <td class="mono small">{{ tx.kobble_transaction_id || '—' }}</td>
                      <td>{{ tx.created_at ? (tx.created_at | date: 'medium') : '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }
  }
</section>
